import React, { useEffect, useState, useCallback } from "react";
import api, { fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { StatusBadge } from "@/components/StatusBadge";
import { Thumb, DateRange, TabBar, Row, fmtDate } from "@/components/list";
import { Button } from "@/components/ui/button";
import { LogOut, Zap, Loader2, ExternalLink, Check, Rocket, LayoutGrid, Film } from "lucide-react";
import { toast } from "sonner";

const TABS = ["In Edit", "In Review", "Done"];
const tabOf = (b) => {
  const s = b.stage || "Not Started";
  if (s === "In Review") return "In Review";
  if (s === "Approved" || s === "Launched") return "Done";
  return "In Edit";
};

const ClientPortal = () => {
  const { user, logout } = useAuth();
  const [briefs, setBriefs] = useState([]);
  const [tab, setTab] = useState("In Review");
  const [from, setFrom] = useState(""); const [to, setTo] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    const params = {}; if (from) params.date_from = from; if (to) params.date_to = to;
    try { const r = await api.get("/briefs", { params }); setBriefs(r.data); }
    catch (e) { toast.error("Failed to load"); }
    setLoading(false);
  }, [from, to]);
  useEffect(() => { load(); }, [load]);

  const setStage = async (id, stage) => {
    setBusy(id + stage);
    try { await api.put(`/briefs/${id}/stage`, { stage }); toast.success(stage === "Approved" ? "Approved!" : "Launched 🚀"); load(); }
    catch (e) { toast.error("Action failed"); }
    setBusy(null);
  };

  const counts = TABS.reduce((a, t) => { a[t] = briefs.filter((b) => tabOf(b) === t).length; return a; }, {});
  const filtered = briefs.filter((b) => tabOf(b) === tab);
  const totalCreatives = briefs.reduce((n, b) => n + (b.num_creatives || 0), 0);

  return (
    <div className="min-h-screen bg-ink-900 text-white">
      <header className="sticky top-0 z-30 backdrop-blur-xl bg-ink-900/80 border-b border-ink-700">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2"><div className="w-9 h-9 rounded-xl bg-brand flex items-center justify-center"><Zap className="w-5 h-5 text-ink-900" fill="#09090B" /></div><span className="font-display text-xl font-bold">SuperPumped Studio</span></div>
          <div className="flex items-center gap-3"><span className="text-zinc-400 text-sm hidden sm:inline">{user?.name}</span>
            <button data-testid="portal-logout" onClick={logout} className="p-2 rounded-lg text-zinc-400 hover:text-white hover:bg-ink-800"><LogOut className="w-5 h-5" /></button></div>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-5 sm:px-8 py-10">
        <p className="text-brand font-semibold uppercase tracking-wide text-sm mb-2">Your briefs</p>
        <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight">Review & approve</h1>

        <div className="grid grid-cols-2 gap-4 mt-6 mb-8 max-w-md">
          <div data-testid="summary-briefs" className="bg-ink-800 border border-ink-700 rounded-2xl p-4 flex items-center gap-3"><LayoutGrid className="w-6 h-6 text-brand" /><div><p className="font-display text-3xl font-bold">{briefs.length}</p><p className="text-zinc-400 text-sm">Briefs</p></div></div>
          <div data-testid="summary-creatives" className="bg-ink-800 border border-ink-700 rounded-2xl p-4 flex items-center gap-3"><Film className="w-6 h-6 text-brand" /><div><p className="font-display text-3xl font-bold">{totalCreatives}</p><p className="text-zinc-400 text-sm">Creatives</p></div></div>
        </div>

        <div className="flex flex-wrap items-center gap-3 justify-between mb-6">
          <TabBar tabs={TABS} value={tab} onChange={setTab} counts={counts} testidPrefix="client-tab" />
          <DateRange from={from} to={to} onFrom={setFrom} onTo={setTo} />
        </div>

        {loading ? <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-brand" /></div> : (
          <div className="space-y-3">
            {filtered.map((b) => (
              <Row key={b.id} testid={`client-brief-${b.id}`}>
                <Thumb round src={b.client_thumbnail ? fileUrl(b.client_thumbnail) : null} alt={b.client_name} />
                <div className="min-w-0 flex-1">
                  <p className="font-semibold truncate">{b.title}</p>
                  <p className="text-zinc-400 text-sm truncate">Created {fmtDate(b.created_at)} • Submitted {fmtDate(b.submitted_at)} • {b.num_creatives || 0} creatives</p>
                </div>
                <StatusBadge status={b.stage} />
                {(b.drive_folder_link || b.google_doc_link) && (
                  <a href={b.drive_folder_link || b.google_doc_link} target="_blank" rel="noreferrer" data-testid={`open-brief-${b.id}`}
                    className="inline-flex items-center gap-1.5 text-sm font-semibold rounded-full px-4 py-2 border border-ink-600 text-brand hover:bg-ink-700 shrink-0"><ExternalLink className="w-4 h-4" /> Open brief</a>
                )}
                {b.stage === "In Review" && <Button data-testid={`client-approve-${b.id}`} onClick={() => setStage(b.id, "Approved")} disabled={!!busy} size="sm" className="rounded-full bg-brand text-ink-900 hover:bg-brand-hover font-semibold shrink-0">{busy === b.id + "Approved" ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Check className="w-4 h-4 mr-1" /> Approve</>}</Button>}
                {b.stage === "Approved" && <Button data-testid={`client-launch-${b.id}`} onClick={() => setStage(b.id, "Launched")} disabled={!!busy} size="sm" className="rounded-full bg-brand text-ink-900 hover:bg-brand-hover font-semibold shrink-0">{busy === b.id + "Launched" ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Rocket className="w-4 h-4 mr-1" /> Launch</>}</Button>}
              </Row>
            ))}
            {filtered.length === 0 && <p className="text-zinc-500 py-10 text-center">Nothing in "{tab}" yet.</p>}
          </div>
        )}
      </div>
    </div>
  );
};

export default ClientPortal;
